import { useMemo, useState } from "react";
import { Wind, RotateCcw } from "@/lib/icons";
import { Button } from "@/components/ui/button";

/**
 * Gas Laws sandbox.
 *
 * Mode A (Boyle / isothermal):  hold T, n constant; vary V → P = nRT/V.
 * Mode B (Charles / isobaric):  hold P, n constant; vary T → V = nRT/P.
 * Mode C (Ideal gas / PV=nRT):  set any three, solve for the fourth.
 */

const R = 0.0821; // L·atm / (mol·K)

type Mode = "boyle" | "charles" | "ideal";

export default function GasLawsLab() {
  const [mode, setMode] = useState<Mode>("boyle");
  const [n, setN] = useState(1.0); // mol
  const [T_C, setT_C] = useState(25); // °C
  const [V, setV] = useState(22.4); // L
  const [P, setP] = useState(1.0); // atm
  const T_K = T_C + 273.15;

  const result = useMemo(() => {
    if (mode === "boyle") {
      // hold T, n; vary V → solve P
      const Pcalc = (n * R * T_K) / V;
      return { variable: "P", value: Pcalc, unit: "atm" };
    }
    if (mode === "charles") {
      const Vcalc = (n * R * T_K) / P;
      return { variable: "V", value: Vcalc, unit: "L" };
    }
    // ideal: just verify PV=nRT residual
    return { variable: "PV/(nRT)", value: (P * V) / (n * R * T_K), unit: "" };
  }, [mode, n, T_K, V, P]);

  const reset = () => {
    setMode("boyle");
    setN(1.0);
    setT_C(25);
    setV(22.4);
    setP(1.0);
  };

  // Visualization: piston with gas particles
  const pistonHeight = Math.max(20, Math.min(220, 220 - V * 5));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
      <div className="rounded-lg border border-border bg-tile-bg p-6 flex flex-col items-center justify-center min-h-[360px]">
        {/* Cylinder */}
        <div className="relative w-32 h-64 border-4 border-foreground/30 rounded-t-md bg-background overflow-hidden">
          {/* Piston */}
          <div
            className="absolute left-0 right-0 bg-foreground/40 border-y-2 border-foreground/60 transition-all duration-300"
            style={{ top: 0, height: `${pistonHeight}px` }}
            aria-hidden="true"
          />
          {/* Gas region with bouncing dots */}
          <div
            className="absolute left-0 right-0 bottom-0 bg-primary/10"
            style={{ top: `${pistonHeight}px` }}
          >
            {Array.from({ length: 14 }).map((_, i) => (
              <span
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-primary"
                style={{
                  left: `${(i * 37) % 90 + 5}%`,
                  top: `${((i * 53) % 80) + 10}%`,
                  opacity: 0.5 + ((i * 17) % 50) / 100,
                }}
                aria-hidden="true"
              />
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3 w-full max-w-md text-center">
          <Stat label="Pressure" value={`${P.toFixed(2)} atm`} />
          <Stat label="Volume" value={`${V.toFixed(2)} L`} />
          <Stat label="Temp" value={`${T_C.toFixed(0)} °C`} />
        </div>

        <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 font-mono text-sm">
          {result.variable} = {result.value.toFixed(3)} {result.unit}
          {mode === "ideal" && (
            <span className="ml-2 text-xs text-muted-foreground">
              (= 1.000 means PV=nRT holds)
            </span>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Mode
          </label>
          <div className="mt-1 grid grid-cols-3 gap-1 rounded-md border border-border p-1 bg-background">
            {(
              [
                ["boyle", "Boyle"],
                ["charles", "Charles"],
                ["ideal", "PV=nRT"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={`text-xs h-7 rounded ${
                  mode === id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {mode === "boyle"
              ? "T and n held constant. Vary V to see how P changes (P ∝ 1/V)."
              : mode === "charles"
              ? "P and n held constant. Vary T to see how V changes (V ∝ T)."
              : "Set P, V, n, T independently - verify PV/(nRT) = 1."}
          </p>
        </div>

        <Slider label="n (mol)" value={n} min={0.1} max={5} step={0.1} onChange={setN} />
        <Slider label="T (°C)" value={T_C} min={-50} max={300} step={5} onChange={setT_C} />
        {(mode === "boyle" || mode === "ideal") && (
          <Slider label="V (L)" value={V} min={1} max={50} step={0.5} onChange={setV} />
        )}
        {(mode === "charles" || mode === "ideal") && (
          <Slider label="P (atm)" value={P} min={0.1} max={5} step={0.1} onChange={setP} />
        )}

        <Button size="sm" variant="outline" onClick={reset} className="gap-1.5">
          <RotateCcw size={14} aria-hidden="true" />
          Reset
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1">
        <Wind size={10} aria-hidden="true" /> {label}
      </div>
      <div className="font-mono text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <label className="font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </label>
        <span className="font-mono text-foreground">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 accent-primary"
      />
    </div>
  );
}